import { useInfiniteQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { RoleGate } from "../components/RoleGate";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { listAuditLogs } from "../services/audit.service";
import { getApiErrorMessage } from "../lib/apiClient";

function AuditLogPageContent(): JSX.Element {
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["audit-logs"],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => listAuditLogs({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const logs = data?.pages.flatMap((page) => page.logs) ?? [];

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Audit Log" }]} />
      <PageTitle
        icon={ScrollText}
        title="Audit Log"
        description="Every action across the platform, newest first. All entries are kept permanently — load more to page back through history."
      />

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}
        {isError && <ErrorState message={getApiErrorMessage(error)} />}
        {!isLoading && !isError && logs.length === 0 && <EmptyState title="No audit entries yet" />}
        {logs.length > 0 && (
          <>
            <div className="card overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
                <thead className="bg-slate-50 dark:bg-slate-700/60">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {logs.map((log) => (
                    <tr key={log.id} className="transition-colors duration-100 hover:bg-slate-100 dark:hover:bg-slate-700/60">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3 dark:text-slate-200">{log.user ? log.user.name : "System / Parent"}</td>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{log.action}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{log.entityType}</td>
                      <td className="px-4 py-3 max-w-xs truncate text-slate-500 dark:text-slate-400">{log.metadata ? JSON.stringify(log.metadata) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-center">
              {hasNextPage ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </button>
              ) : (
                <p className="text-xs text-slate-400 dark:text-slate-500">No more entries.</p>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default function AuditLogPage(): JSX.Element {
  return (
    <RoleGate roles={["SUPER_ADMIN"]}>
      <AuditLogPageContent />
    </RoleGate>
  );
}
